import { Injectable, Injector } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { NgxSpinnerService } from "ngx-spinner";
import {ErrorEventService, SharedError} from "../services/error.event.service";
import { environment } from 'src/environments/environment';
import { SweetAlertService } from '../services/sweet-alert.service';
import { ApiErrorService, DisplayableError } from '../services/api-error.service';
import { SessionTerminationService } from '../services/session-termination.service';
import { SKIP_GLOBAL_ERROR_ALERT } from './http-error.context';

/**
 * Single place that reports API failures to the user.
 *
 * The API answers HTTP 200 even when a request fails (`result: false`), so both the `tap` and the
 * `catchError` path below can carry a failure. Components own success, warning and field-level
 * rendering; they must not raise their own error dialog for a failure handled here, or the user
 * sees the same problem twice. A component that genuinely needs to render the failure itself opts
 * out with SKIP_GLOBAL_ERROR_ALERT.
 */
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  /**
   * Guards against a burst of dialogs when several requests fail together (e.g. a page load firing
   * four calls against a dead API). Cleared when the user dismisses the current one.
   */
  private alertOpen = false;

  constructor(private spinner: NgxSpinnerService,
              private errorEventService: ErrorEventService,
              private sweetAlertService: SweetAlertService,
              private apiErrorService: ApiErrorService,
              private injector: Injector) {}

  /**
   * Resolved on use, not injected. SessionTerminationService reaches ChatService, which needs
   * HttpClient -- and HttpClient is what builds this interceptor, so taking it in the constructor
   * risks a cyclic dependency. By the time a response fails, HttpClient exists and this is safe.
   */
  private get sessionTermination(): SessionTerminationService {
    return this.injector.get(SessionTerminationService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if this is the specific passport verification URL with mock_auth_code_12345
    const isPassportVerifyWithMockCode = req.url.includes(`${environment.apiConfig.uri}/api/user/verify?code=`);

    // If it's the specific URL, bypass the error handling
    if (isPassportVerifyWithMockCode) {
      return next.handle(req);
    }

    const skipAlert = req.context.get(SKIP_GLOBAL_ERROR_ALERT);

    return next.handle(req).pipe(
      // Handle successful responses with potential error-like payloads
      tap((event: HttpEvent<any>) => {
        if (event instanceof HttpResponse) {
          const body = event.body;

          if (this.apiErrorService.isFailureBody(body)) {
            this.spinner.hide();

            if (!skipAlert) {
              const displayable = this.apiErrorService.fromBody(body);
              this.show(displayable, () => this.handleApplicationError({
                message: displayable.title,
                errorType: 'Application',
                details: body?.messages ?? []
              }));
            }
          }
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this.spinner.hide(); // Ensure spinner is hidden

        console.error('HTTP Error:', error, 'traceId:', this.apiErrorService.traceIdOf(error));

        // The account was deactivated in CRM while the user was signed in. Owns the whole response:
        // the generic dialog below would only stack a vaguer message on top of the sign-out notice.
        if (error.status === 401 && error.error?.code === 'USER_DEACTIVATED') {
          this.sessionTermination.deactivated();
          return throwError(() => error);
        }

        if (!skipAlert) {
          const displayable = this.apiErrorService.fromHttpError(error);
          this.show(displayable, () => {
            const sharedError: SharedError = {
              message: displayable.title,
              status: error.status,
              errorType: 'HTTP'
            };

            this.errorEventService.emitErrorEvent(sharedError);
          });
        }

        return throwError(() => error);
      })
    );
  }

  private show(displayable: DisplayableError, onConfirm: () => void): void {
    if (this.alertOpen) {
      return;
    }

    this.alertOpen = true;
    this.sweetAlertService.fire({
      title: displayable.title,
      html: displayable.html,
      icon: 'error',
      confirmButtonText: 'OK'
    }).then((result) => {
      this.alertOpen = false;

      if (result.isConfirmed) {
        onConfirm();
      }
    }, () => {
      this.alertOpen = false;
    });
  }

  private handleApplicationError(error: SharedError): void {
    console.error('Application error handled:', error);


    this.errorEventService.emitErrorEvent(error);
  }

}
